import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Select,
  Spin,
  Alert,
  Table,
  Tooltip,
  Typography,
  DatePicker,
} from 'antd';
import {
  CheckCircleOutlined,
  RobotOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Pie, Line } from '@ant-design/charts';
import dayjs from 'dayjs';
import { getDashboardSummary, getDashboardTrend, getDashboardAccuracy } from '../services/api';
import { useBranch } from '../components/BranchContext';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const MONO = {
  lightest: '#D6E4F7',
  light: '#7FA3D4',
  medium: '#4A72A8',
  dark: '#1A3C7A',
};

const SHORT = {
  'No Changes': 'No Changes',
  'Incidental': 'Incidental',
  'Minor': 'Minor',
  'Major': 'Major',
  'Out of Scope': 'Out of Scope',
};

const cardStyle = {
  borderRadius: 12,
  border: '1px solid #eef1f5',
  boxShadow: 'none',
};

const kpiBodyStyle = { padding: 24, height: 132, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' };

const Dashboard = () => {
  const yesterday = dayjs().subtract(1, 'day');
  const today = dayjs();
  const [dateRange, setDateRange] = useState([yesterday, yesterday]);
  const [trendDays, setTrendDays] = useState(7);
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { branchParam } = useBranch();

  const dateParams = () => {
    const params = {};
    if (dateRange && dateRange[0] && dateRange[1]) {
      params.date_from = dateRange[0].format('YYYY-MM-DD');
      params.date_to = dateRange[1].format('YYYY-MM-DD');
    }
    if (branchParam) params.branch = branchParam;
    return params;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = dateParams();
      const trendParams = branchParam ? { branch: branchParam } : {};
      const [s, t, a] = await Promise.all([
        getDashboardSummary(params),
        getDashboardTrend(trendDays, trendParams),
        getDashboardAccuracy(params),
      ]);
      setSummary(s);
      setTrend(t);
      setAccuracy(a);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [dateRange, trendDays, branchParam]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rangeLabel = (() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return '';
    const [a, b] = dateRange;
    if (a.isSame(b, 'day')) return a.format('MMM D, YYYY');
    return `${a.format('MMM D')} – ${b.format('MMM D, YYYY')}`;
  })();

  const pageHeader = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        marginBottom: 20,
        gap: 10,
      }}
    >
      <Text style={{ color: '#8895a7', fontSize: 13 }}>Date range</Text>
      <RangePicker
        value={dateRange}
        onChange={setDateRange}
        allowClear={false}
        format="YYYY-MM-DD"
        presets={[
          { label: 'Yesterday → Today', value: [yesterday, today] },
          { label: 'Yesterday', value: [yesterday, yesterday] },
          { label: 'Today', value: [today, today] },
          { label: 'Last 7 days', value: [dayjs().subtract(7, 'day'), today] },
          { label: 'Last 30 days', value: [dayjs().subtract(30, 'day'), today] },
        ]}
      />
    </div>
  );

  if (loading) {
    return (
      <div>
        {pageHeader}
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: '#8895a7' }}>Loading dashboard...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        {pageHeader}
        <Alert type="error" message="Error" description={error} showIcon style={{ borderRadius: 12 }} />
      </div>
    );
  }

  if (!summary) return pageHeader;

  const { total_cases, classification_distribution } = summary;

  const classLookup = {};
  (classification_distribution || []).forEach((i) => { classLookup[i.classification] = i; });
  const noChanges = classLookup['No Changes']?.count || 0;

  const pct = (n) => (total_cases ? ((n / total_cases) * 100).toFixed(1) : '0.0');
  const noChangesPct = pct(noChanges);
  const accuracyPct = accuracy?.overall?.accuracy_pct;

  const pieData = (classification_distribution || []).map((i) => ({
    classification: SHORT[i.classification] || i.classification,
    value: i.count,
  }));

  const pieConfig = {
    data: pieData,
    angleField: 'value',
    colorField: 'classification',
    innerRadius: 0.68,
    radius: 0.88,
    scale: { color: { range: [MONO.lightest, MONO.light, MONO.medium, MONO.dark, '#9ca3af'] } },
    label: {
      text: (d) => `${d.value}`,
      style: { fontWeight: 600, fontSize: 12, fill: MONO.dark },
    },
    legend: { position: 'bottom', itemSpacing: 20 },
    tooltip: { title: 'classification' },
    statistic: {
      title: { content: 'Total', style: { fontSize: 12, color: '#8895a7' } },
      content: {
        content: (total_cases || 0).toLocaleString(),
        style: { fontSize: 22, color: MONO.dark, fontWeight: 700 },
      },
    },
  };

  // Single-line accuracy % trend — computed per day from counts
  const trendItems = trend?.trend || [];
  const accuracyTrend = trendItems.map((d) => {
    const total =
      (d['No Changes'] || 0) +
      (d['Incidental'] || 0) +
      (d['Minor'] || 0) +
      (d['Major'] || 0);
    const major = d['Major'] || 0;
    return {
      date: d.date,
      accuracy: total > 0 ? Number((((total - major) / total) * 100).toFixed(1)) : 0,
      cases: total,
    };
  });

  const lineConfig = {
    data: accuracyTrend,
    xField: 'date',
    yField: 'accuracy',
    smooth: true,
    point: null,
    line: { style: { stroke: MONO.dark, lineWidth: 2.5 } },
    area: { style: { fill: `l(270) 0:${MONO.lightest} 1:${MONO.dark}`, fillOpacity: 0.25 } },
    axis: {
      x: { label: null, line: null, tick: null },
      y: {
        label: { formatter: (v) => `${v}%`, style: { fontSize: 10, fill: '#b4bcc9' } },
        line: null,
        tick: null,
        grid: { line: { style: { stroke: '#eef1f5', lineDash: [3, 3], lineWidth: 1 } } },
      },
    },
    tooltip: {
      items: [
        { field: 'accuracy', name: 'Accuracy', valueFormatter: (v) => `${v}%` },
        { field: 'cases', name: 'Cases' },
      ],
    },
  };

  // Classification bar-bar data
  const classOrderKeys = ['No Changes', 'Incidental', 'Minor', 'Major'];
  const classColors = {
    'No Changes': '#B5C7E6',
    'Incidental': '#7FA3D4',
    'Minor': '#4A72A8',
    'Major': '#1A3C7A',
  };
  const classTotal = classOrderKeys.reduce((s, k) => s + (classLookup[k]?.count || 0), 0);

  const avgAccuracy = accuracyTrend.length
    ? (accuracyTrend.reduce((s, d) => s + d.accuracy, 0) / accuracyTrend.length).toFixed(1)
    : null;

  return (
    <div>
      {pageHeader}

      {/* KPI row — 3 equal tiles */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} md={8}>
          <Card style={cardStyle} bodyStyle={kpiBodyStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileTextOutlined style={{ color: '#8895a7', fontSize: 18 }} />
              <Text style={{ color: '#8895a7', fontSize: 13 }}>Total Cases</Text>
            </div>
            <Statistic
              value={(total_cases || 0).toLocaleString()}
              valueStyle={{ color: MONO.dark, fontSize: 32, fontWeight: 700, lineHeight: 1 }}
            />
            <div style={{ fontSize: 12, color: '#aab4c0' }}>in selected range</div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card style={cardStyle} bodyStyle={kpiBodyStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <RobotOutlined style={{ color: '#8895a7', fontSize: 18 }} />
              <Text style={{ color: '#8895a7', fontSize: 13 }}>
                AI Accuracy{' '}
                <Tooltip title="Percentage of cases without Major discrepancies.">
                  <InfoCircleOutlined style={{ fontSize: 11, color: '#bbb' }} />
                </Tooltip>
              </Text>
            </div>
            <Statistic
              value={accuracyPct != null ? accuracyPct.toFixed(1) : '--'}
              suffix={accuracyPct != null ? '%' : ''}
              valueStyle={{ color: MONO.medium, fontSize: 32, fontWeight: 700, lineHeight: 1 }}
            />
            <div style={{ fontSize: 12, color: '#aab4c0' }}>
              {accuracy?.overall?.major || 0} major of {accuracy?.overall?.total || 0}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card style={cardStyle} bodyStyle={kpiBodyStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircleOutlined style={{ color: '#8895a7', fontSize: 18 }} />
              <Text style={{ color: '#8895a7', fontSize: 13 }}>No Changes Rate</Text>
            </div>
            <Statistic
              value={noChangesPct}
              suffix="%"
              valueStyle={{ color: MONO.medium, fontSize: 32, fontWeight: 700, lineHeight: 1 }}
            />
            <div style={{ fontSize: 12, color: '#aab4c0' }}>
              {noChanges.toLocaleString()} of {(total_cases || 0).toLocaleString()} cases
            </div>
          </Card>
        </Col>
      </Row>

      {/* Classification + Trend side-by-side */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} lg={12}>
          <Card
            title={<Text strong style={{ color: MONO.dark }}>Classification Distribution</Text>}
            size="small"
            style={{ ...cardStyle, height: '100%' }}
            headStyle={{ border: 'none', paddingTop: 14 }}
            bodyStyle={{ paddingTop: 4, paddingBottom: 22 }}
          >
            {classTotal > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', gap: 18, paddingTop: 16, paddingBottom: 8, minHeight: 240 }}>
                {classOrderKeys.map((k) => {
                  const c = classLookup[k]?.count || 0;
                  const p = classTotal > 0 ? (c / classTotal) * 100 : 0;
                  return (
                    <div key={k}>
                      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: classColors[k], flexShrink: 0 }} />
                          <Text style={{ color: '#2b3a55', fontSize: 13, fontWeight: 500 }}>{SHORT[k]}</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                          <Text style={{ color: '#8895a7', fontSize: 12 }}>{p.toFixed(1)}%</Text>
                          <Text style={{ color: MONO.dark, fontSize: 15, fontWeight: 700, minWidth: 40, textAlign: 'right' }}>
                            {c.toLocaleString()}
                          </Text>
                        </div>
                      </div>
                      <div style={{ height: 6, background: '#f2f4f7', borderRadius: 3, overflow: 'hidden' }}>
                        <div
                          style={{
                            height: '100%',
                            width: `${p}%`,
                            background: classColors[k],
                            borderRadius: 3,
                            transition: 'width 0.4s ease',
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: '#aab4c0', padding: '80px 0' }}>
                No cases in selected date range
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={<Text strong style={{ color: MONO.dark }}>AI Accuracy Trend</Text>}
            size="small"
            style={{ ...cardStyle, height: '100%' }}
            headStyle={{ border: 'none', paddingTop: 14 }}
            bodyStyle={{ paddingTop: 4 }}
            extra={
              <Select
                value={trendDays}
                onChange={setTrendDays}
                style={{ width: 140 }}
                size="small"
                bordered={false}
                options={[
                  { value: 7, label: 'Last 7 days' },
                  { value: 14, label: 'Last 14 days' },
                  { value: 30, label: 'Last 30 days' },
                  { value: 60, label: 'Last 60 days' },
                  { value: 90, label: 'Last 90 days' },
                ]}
              />
            }
          >
            <div style={{ height: 250 }}>
              {accuracyTrend.length > 0 ? (
                <Line {...lineConfig} />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#aab4c0' }}>
                  No trend data
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Accuracy by study type */}
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card
            title={<Text strong style={{ color: MONO.dark }}>Accuracy by Study Type</Text>}
            size="small"
            style={cardStyle}
            headStyle={{ border: 'none', paddingTop: 14, paddingBottom: 3 }}
            bodyStyle={{ paddingTop: 12, paddingBottom: 20 }}
          >
            <Table
              dataSource={accuracy?.by_study_type || []}
              rowKey="study_type"
              size="small"
              pagination={false}
              columns={[
                {
                  title: 'Study Type',
                  dataIndex: 'study_type',
                  key: 'study_type',
                  render: (t) => <Text strong style={{ fontSize: 13 }}>{t}</Text>,
                },
                {
                  title: 'Cases',
                  dataIndex: 'total',
                  key: 'total',
                  width: 100,
                  sorter: (a, b) => a.total - b.total,
                  render: (v) => <Text>{v.toLocaleString()}</Text>,
                },
                {
                  title: 'Major',
                  dataIndex: 'major',
                  key: 'major',
                  width: 100,
                  sorter: (a, b) => a.major - b.major,
                  render: (v) => <Text style={{ color: MONO.dark }}>{v.toLocaleString()}</Text>,
                },
                {
                  title: 'Accuracy',
                  dataIndex: 'accuracy_pct',
                  key: 'accuracy_pct',
                  width: 120,
                  defaultSortOrder: 'descend',
                  sorter: (a, b) => a.accuracy_pct - b.accuracy_pct,
                  render: (v) => <Text strong style={{ color: MONO.medium }}>{v?.toFixed(1)}%</Text>,
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
